import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, ShieldCheck, Hourglass, AlertTriangle, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RazorpayButton } from "@/components/RazorpayButton";

// Both planner and joinee now pay securely via Razorpay
const DEPOSIT_WINDOW = {
  planner: ["pending_deposit", "open", "joinee_selected", "awaiting_deposits"],
  joinee: ["joinee_selected", "awaiting_deposits"],
};

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export const PlanActions = ({ plan, role, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const amount = role === "planner" ? plan.planner_deposit : plan.joinee_deposit;
  const dep = plan.my_deposit;
  const canPay = DEPOSIT_WINDOW[role].includes(plan.status) && (!dep || dep.status === "rejected");

  const markCompleted = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plans/${plan.plan_id}/complete`);
      toast.success(data.status === "completed" ? "Meetup completed — deposits will be refunded by admin" : "Marked as completed");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark completed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#D4AF37]/15 space-y-3" data-testid="plan-actions">
      {role === "planner" && plan.status === "pending_deposit" && (
        <p className="flex items-center gap-2 text-xs text-[#FFB300]" data-testid="pending-deposit-hint">
          <EyeOff className="w-4 h-4" strokeWidth={1.5} />
          Your table is hidden from the circle until the deposit is paid
        </p>
      )}
      {dep?.status === "pending_review" && (
        <p className="flex items-center gap-2 text-xs text-[#FFB300]" data-testid="deposit-status-pending">
          <Hourglass className="w-4 h-4" strokeWidth={1.5} />
          Deposit of {inr(dep.amount)} submitted — awaiting admin verification
        </p>
      )}
      {dep?.status === "approved" && (
        <p className="flex items-center gap-2 text-xs text-[#4CAF50]" data-testid="deposit-status-approved">
          <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
          Deposit of {inr(dep.amount)} {dep.method === "razorpay" ? "paid via Razorpay" : "approved"}
          {dep.refunded ? " · refunded" : ""}
        </p>
      )}
      {dep?.status === "rejected" && (
        <p className="flex items-center gap-2 text-xs text-[#E53935]" data-testid="deposit-status-rejected">
          <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
          Deposit rejected — please pay again securely via Razorpay below
        </p>
      )}
      {canPay && (role === "planner" || role === "joinee") && (
        <RazorpayButton
          plan={plan}
          amount={amount}
          role={role}
          onSuccess={onChanged}
          label={
            role === "planner" && plan.status === "pending_deposit"
              ? `Pay 40% Deposit to Go Live · ${inr(amount)}`
              : `Pay ${role === "planner" ? "40%" : "25%"} Deposit · ${inr(amount)}`
          }
        />
      )}
      {plan.whatsapp_contact && (
        <Button
          asChild
          data-testid="whatsapp-connect-button"
          className="w-full bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md transition-colors"
        >
          <a href={`https://wa.me/${plan.whatsapp_contact}`} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-4 h-4 mr-2" strokeWidth={2} />
            Connect on WhatsApp
          </a>
        </Button>
      )}
      {plan.status === "confirmed" &&
        (plan.my_completed ? (
          <p className="flex items-center gap-2 text-xs text-[#A0A0A0]" data-testid="completion-waiting">
            <Hourglass className="w-4 h-4" strokeWidth={1.5} />
            You marked completed — awaiting the other party
          </p>
        ) : (
          <Button
            variant="outline"
            onClick={markCompleted}
            disabled={busy}
            data-testid="mark-completed-button"
            className="w-full border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F1D570] bg-transparent rounded-md transition-colors"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Mark Completed
          </Button>
        ))}
      {plan.status === "completed" && (
        <p className="flex items-center gap-2 text-xs text-[#4CAF50]" data-testid="completion-done">
          <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
          Meetup completed
        </p>
      )}
    </div>
  );
};
